/**
 * Serializes writes to a single listing.
 *
 * The pricing fields save on blur (PATCH) while the toolbar saves the whole
 * listing (PUT). Clicking "Save listing" while a price field has focus fires
 * both, because the browser order is mousedown -> blur -> click: the PATCH and
 * the PUT end up in flight against the same row at the same time, and whichever
 * lands last wins. Chaining them per listing removes the overlap, so the save
 * button always writes on top of the field save rather than racing it.
 */
const chains = new Map<string, Promise<unknown>>();

/**
 * Runs `task` once every previously queued write for this listing has settled.
 *
 * Rejections propagate to the caller but never break the chain — a failed save
 * must not strand every later save behind it.
 */
export function enqueueListingWrite<T>(listingId: string | undefined, task: () => Promise<T>): Promise<T> {
    if (!listingId) {
        return task();
    }

    const previous = chains.get(listingId) ?? Promise.resolve();

    const result = previous.then(task, task);

    // The tail is the settled form, so the next write waits for this one to
    // finish without inheriting its rejection.
    const tail = result.then(() => undefined, () => undefined);

    chains.set(listingId, tail);

    void tail.then(() => {
        // Drop the entry once this write is the last one queued, so the map
        // does not grow for the lifetime of the session.
        if (chains.get(listingId) === tail) {
            chains.delete(listingId);
        }
    });

    return result;
}

/**
 * Test seam — the queue is module state, so suites that assert ordering need a
 * way back to a clean slate.
 */
export function resetListingWriteQueue(): void {
    chains.clear();
}
